import { axiosInstance } from "api/axios";
import { IFileInitUploadResponse } from "api/fileService";
import { IEncryptedFile } from "components/CreateDossierModal/typings";
import {
  ISingleBinaryKeys,
  generateSingleBinary,
} from "./GenerateSingleBinary";
import encryptFile from "./encryptFile";
import { readFileAsBlob } from "./readFileAsBlob";
import { createChunks } from "./createChunksFromBlob";
import { logger } from "./logger";
import * as openpgp from "openpgp";
import { sha3_512 } from "js-sha3";

const hexToBase64 = (hexstring: string) => {
  return btoa(
    hexstring
      .match(/\w{2}/g)
      .map(function (a) {
        return String.fromCharCode(parseInt(a, 16));
      })
      .join(""),
  );
};

export const calculateSHA3Hash = async (arrayBuffer: ArrayBuffer) => {
  //const k = await sha3(new Uint8Array(arrayBuffer), 512);
  const k = sha3_512(arrayBuffer);
  console.log("Hex hash", k);

  return hexToBase64(k);
};

const encryptFiles = async (
  files: File[] | Blob[],
  keys: ISingleBinaryKeys,
  callback?: (index: number, progress: number) => void,
) => {
  const blobs = await Promise.all(
    files.map(async (file: File, index: number) => {
      const fileAndBlob = await readFileAsBlob(file);
      const returnedFile = fileAndBlob[0];
      const returnedBlob = fileAndBlob[1];

      callback?.(index, -1);

      console.time("EncryptFile");
      const encryptedFile = await encryptFile(
        returnedBlob.target?.result,
        keys,
      );
      console.timeEnd("EncryptFile");

      console.time("CryptoJS.SHA3");
      const hash = await calculateSHA3Hash(
        returnedBlob.target?.result as ArrayBuffer,
      );
      console.timeEnd("CryptoJS.SHA3");

      return {
        orginalFile: returnedFile,
        encryptedFile: encryptedFile,
        blob: returnedBlob,
        hash: hash,
      };
    }),
  );

  return blobs;
};

const Files = async (
  files: File[] | Blob[],
  callback?: (index: number, progress: number) => void,
) => {
  const blobs = await Promise.all(
    files.map(async (file: File, index: number) => {
      console.time("ReadFileAsBlob");

      callback?.(index, -1);

      const fileAndBlob = await readFileAsBlob(file);
      console.timeEnd("ReadFileAsBlob");
      const returnedFile = fileAndBlob[0];
      const returnedBlob = fileAndBlob[1];

      console.time("CryptoJS.SHA3");
      const hash = await calculateSHA3Hash(
        returnedBlob.target?.result as ArrayBuffer,
      );
      console.timeEnd("CryptoJS.SHA3");
      logger.log("File hash", hash);
      return {
        orginalFile: returnedFile,
        blob: returnedBlob,
        hash: hash,
      };
    }),
  );

  return blobs;
};

export const handleCreate = async (
  files: File[] | Blob[],
  caseId: string | undefined,
  publicKey: openpgp.key.KeyResult,
  useEncrypt: boolean,
  useDecrypt: boolean,
  fileName?: string,
  fileType?: string,
  callback?: (index: number, progress: number) => void,
  // czy dla każdego pliku wygenerować klucz
  // używane tylko na potrzeby Dossier
  generateObjectKey: boolean = true,
  contactForm: boolean = false,
  // token przesyłki formularza kontaktowego (Sid z /init) — nagłówek X-Contact-Sid
  token?: string,
) => {
  if (!files.length) {
    return;
  }

  // Creating keys and uploading to database also in this step we encrypt all files from hook
  const response: any = null;
  if (useEncrypt) {
    let keys: ISingleBinaryKeys = null;
    let keyId: string = null;

    if (generateObjectKey) {
      keys = await generateSingleBinary(null, null, publicKey);
      const response = await axiosInstance.post("/keys/object", {
        CaseId: caseId,
        PrivateKey: keys.PrivateKey,
        PublicKey: keys.PublicKey,
      });

      keyId = response.data.Id;
    } else {
      keys = {
        PublicKey: null,
        PrivateKey: null,
        cleanPrivateKey: null,
        cleanPublicKey: publicKey,
      };
    }

    console.time("FilesEncryption");
    const encryptedFiles = await encryptFiles(files, keys, callback);
    console.timeEnd("FilesEncryption");

    console.time("Upload");
    const uris = await initUpload(
      keyId,
      encryptedFiles,
      useDecrypt,
      fileName,
      fileType,
      callback,
      contactForm,
      token,
    );
    console.timeEnd("Upload");
    return uris;
  }

  console.time("FilesCollection");
  const encryptedFiles = await Files(files, callback);
  console.timeEnd("FilesCollection");

  console.time("Upload");
  const uris = await initUpload(
    response?.data?.Id,
    encryptedFiles,
    useDecrypt,
    fileName,
    fileType,
    callback,
    contactForm,
    token,
  );
  console.timeEnd("Upload");
  return uris;
};

// Initialize upload sending information about files
const initUpload = async (
  keyId: string,
  encryptedFiles: IEncryptedFile[],
  useDecrypt: boolean,
  fileName?: string,
  fileType?: string,
  callback?: (index: number, progress: number) => void,
  contactForm?: boolean,
  token?: string,
) => {
  const cfConfig =
    contactForm && token ? { headers: { "X-Contact-Sid": token } } : undefined;
  return await Promise.all(
    encryptedFiles.map(async (file: IEncryptedFile, fileIndex) => {
      const response = await axiosInstance.post(
        !contactForm ? "/file/init" : "/contactform/upload",
        {
          ContentType: fileType ? fileType : file.orginalFile.type,
          FileName: fileName ? fileName : file.orginalFile.name,
          Hash: file.hash,
          IsSigned: false,
          KeyId: keyId ?? null,
          Size: file.orginalFile.size,
        },
        cfConfig,
      );
      await getLink(
        response.data,
        keyId,
        file,
        fileIndex,
        callback,
        contactForm,
        token,
      );
      return { keyId: keyId, uri: response.data.Uri, encrypted: useDecrypt };
    }),
  );
};

// Getting link to S3
const getLink = async (
  uploadResponse: IFileInitUploadResponse,
  keyId: string,
  file: IEncryptedFile,
  fileIndex: number,
  callback?: (index: number, progress: number) => void,
  contactForm?: boolean,
  token?: string,
) => {
  const cfConfig =
    contactForm && token ? { headers: { "X-Contact-Sid": token } } : undefined;
  const chunks = createChunks(
    new Blob([file.encryptedFile ?? file.orginalFile]),
  );

  const links: string[] = [];

  for (const chunk in chunks) {
    const response = await axiosInstance.get(
      !contactForm
        ? `/file/part?uri=${uploadResponse.Uri}&partNumber=${
            Number(chunk) + 1
          }&uploadId=${uploadResponse.UploadId}`
        : `/contactform/part?uri=${uploadResponse.Uri}&partNumber=${
            Number(chunk) + 1
          }&uploadId=${uploadResponse.UploadId}`,
      cfConfig,
    );
    links.push(response.data);
  }
  await uploadFile(
    uploadResponse.Uri,
    uploadResponse.UploadId,
    links,
    keyId,
    file,
    fileIndex,
    callback,
    contactForm,
    token,
  );
};

const uploadFile = async (
  uri: string,
  uploadId: string,
  links: string[],
  keyId: string,
  file: IEncryptedFile,
  fileIndex: number,
  callback?: (index: number, progress: number) => void,
  contactForm?: boolean,
  token?: string,
) => {
  const chunks = createChunks(
    new Blob([file.encryptedFile ?? file.orginalFile]),
  );
  const etags: string[] = [];

  let sendedChunk = 0;
  for (const chunk of chunks) {
    const etag = await sendChunk(
      links[sendedChunk],
      chunk,
      sendedChunk + 1,
      chunks.length,
      fileIndex,
      callback,
    );
    etags.push(etag as string);
    sendedChunk++;
  }

  axiosInstance.post(
    !contactForm ? "/file/confirm" : "/contactform/confirm",
    {
      UploadId: uploadId,
      Uri: uri,
      Parts: etags.map((etag, index) => ({ Etag: etag, PartNumber: index + 1 })),
      // Parts: [{ ETag: etag!, PartNumber: 1 }],
    },
    contactForm && token ? { headers: { "X-Contact-Sid": token } } : undefined,
  );
};

const sendChunk = async (
  link: string,
  chunk: Blob,
  actualChunk: number,
  maxChunk: number,
  fileIndex: number,
  callback?: (index: number, progress: number) => void,
) => {
  return new Promise((resolve) => {
    const xhr = new XMLHttpRequest();

    xhr.open("PUT", link, true);
    xhr.responseType = "blob";

    // Oczekujemy, że AWS S3 może zwrócić 200 OK, 201 Created lub 204 No Content w odpowiedzi na zapytanie PUT
    xhr.onload = function () {
      if (xhr.status === 200 || xhr.status === 201 || xhr.status === 204) {
        const etag = this.getResponseHeader("ETag");
        // etags.push(etag!);
        resolve(etag?.replaceAll('"', ""));
      } else {
        console.error(
          "Wystąpił błąd podczas wysyłania pliku do AWS S3. Status:",
          xhr.status,
        );
      }
    };

    xhr.upload.onprogress = (e) => {
      //
      // const filePercent = (e.loaded / e.total) * 100
      const filePercent =
        ((actualChunk - 1 + e.loaded / e.total) / maxChunk) * 100;
      callback?.(fileIndex, filePercent);
    };

    xhr.send(chunk);
  });
};
